/* eslint-disable complexity */
import { ProductDetail } from '@grpc-clients/external/mercury/v1/attraction/common/product-detail.pb';
import { defaultDurationFilters, defaultRatingFilters, defaultTimeFilters } from '@shared/constants/filter';
import { ActivitiesOrdering, ActivitiesPaging } from '@shared/models/activities.model';
import { ActivityFilter, ActivityFilterDefault } from '@shared/models/filter.model';
import { environment } from 'environment';
import * as fromActions from '../actions/travel-activities-search.actions';

export const activitiesFeatureKey = 'activities';

export interface ActivitiesState {
  activities: ProductDetail[];
  filterState: ActivityFilters;
  loaded: boolean;
  loading: boolean;
  sort: ActivitiesOrdering;
  paging: ActivitiesPaging;
}

export interface ActivityFilters {
  searchByName: string;
  price: [number, number];
  ratings: ActivityFilter<string>[];
  timeOfDays: ActivityFilter<string>[];
  durations: ActivityFilter<string>[];
  specials: ActivityFilter<number>[];
  categories: ActivityFilter<number>[];
}

const addDefaults = <T>(filter: ActivityFilterDefault<T>) => ({ ...filter, enabled: true, selected: false });

const filterStateInitialValue: ActivityFilters = {
  searchByName: '',
  price: null,
  ratings: defaultRatingFilters.map(addDefaults),
  timeOfDays: defaultTimeFilters.map(addDefaults),
  durations: defaultDurationFilters.map(addDefaults),
  specials: [],
  categories: [],
};

export const initialState: ActivitiesState = {
  activities: [],
  filterState: filterStateInitialValue,
  loading: false,
  loaded: false,
  sort: 1,
  paging: {
    start: 0,
    end: 12,
    index: 0,
    size: 12,
  },
};

export function reducer(state = initialState, action: fromActions.ActivitiesSearchActions): ActivitiesState {
  // correct environment config can be read only after bootstrap
  const getFilterStateInitialValue = (): ActivityFilters => {
    const defaultSpecialFilters: ActivityFilter<number>[] = environment.activities.specialFilters;

    return {
      ...filterStateInitialValue,
      categories: state.filterState.categories.map(addDefaults),
      specials: defaultSpecialFilters.map(addDefaults),
    };
  };

  switch (action.type) {
    case fromActions.GET_ACTIVITIES_SEARCH_RESULTS:
      return {
        ...state,
        loading: true,
        loaded: false,
      };

    case fromActions.GET_ACTIVITIES_SEARCH_RESULTS_SUCCESS: {
      const results = action.payload;

      return {
        ...state,
        activities: results,
        loading: false,
        loaded: true,
      };
    }
    case fromActions.CLEAR_ACTIVITIES_SEARCH_RESULTS:
      return {
        ...initialState,
        filterState: getFilterStateInitialValue(),
      };

    case fromActions.GET_ACTIVITIES_SEARCH_RESULTS_FAILURE:
      return {
        ...state,
        activities: [],
        loading: false,
        loaded: true,
      };
    case fromActions.SET_ACTIVITIES_SORT:
      return {
        ...state,
        paging: initialState.paging,
        sort: action.payload,
      };
    case fromActions.SET_ACTIVITIES_FILTER:
      return {
        ...state,
        paging: initialState.paging,
        filterState: action.filter ? { ...state.filterState, ...action.filter } : getFilterStateInitialValue(),
      };
    case fromActions.SET_ACTIVITIES_PAGING:
      return {
        ...state,
        paging: action.paging,
      };
    case fromActions.CLEAR_ACTIVITIES_FILTER: {
      let newValue: ActivityFilter | [number, number];
      if (action.name === 'price') {
        newValue = null;
      } else {
        newValue = state.filterState[action.name].map((a: ActivityFilter) => ({ ...a, selected: false }));
      }
      return {
        ...state,
        filterState: {
          ...state.filterState,
          [action.name]: newValue,
        },
      };
    }
    case fromActions.ACTIVITIES_SEARCH_BY_NAME_FILTER_CHANGE: {
      return {
        ...state,
        filterState: {
          ...state.filterState,
          searchByName: action.searchQuery,
        },
      };
    }
    case fromActions.ACTIVITIES_MULTIPLE_CHOICE_FILTER_CHANGE: {
      const filters = state.filterState[action.name].map((filter: ActivityFilter) =>
        filter.value === action.value ? { ...filter, selected: action.checked } : filter
      );

      return {
        ...state,
        paging: initialState.paging,
        filterState: { ...state.filterState, [action.name]: filters },
      };
    }
    case fromActions.GET_ACTIVITIES_CATEGORIES_SUCCESS: {
      let categories: ActivityFilter<number>[];

      if (action.keepSelected && state.filterState.categories?.length) {
        const selectedFilterValues = state.filterState.categories
          .filter((filter) => filter.selected)
          .map((filter) => filter.value);
        categories = action.categoryFilter.map((filter) => ({
          ...filter,
          selected: selectedFilterValues.includes(filter.value),
        }));
      } else {
        categories = action.categoryFilter;
      }

      return {
        ...state,
        filterState: { ...state.filterState, categories },
      };
    }
    case fromActions.GET_ACTIVITIES_CATEGORIES_FAILURE: {
      return {
        ...state,
        filterState: { ...state.filterState, categories: null },
      };
    }
    default:
      return state;
  }
}

export const getActivitiesData = (state: ActivitiesState) => state.activities;
export const getActivitiesLoaded = (state: ActivitiesState) => state.loaded;
export const getActivitiesLoading = (state: ActivitiesState) => state.loading;
export const getActivitiesSortType = (state: ActivitiesState) => state.sort;
